import React, { useState, useEffect } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Info } from 'lucide-react';
import { getApiKey } from '@/lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}

interface DimensionExplanation {
  dimension: string;
  score: number;
  threshold: number;
  status: 'normal' | 'warning' | 'critical';
  explanation: string;
  recommendations: string[];
}

interface AnomalyStatus {
  active: boolean;
  overallRisk: 'low' | 'medium' | 'high' | 'critical' | 'uncalibrated';
  compositeScore: number;
  flaggedDimensions: DimensionExplanation[];
  lastUpdated: string;
  sessionId?: string;
  baselineSize?: number;
  anomalyCount?: number;
  calibrated?: boolean;
}

const RISK_CONFIG: Record<AnomalyStatus['overallRisk'], { label: string; color: string; borderColor: string; icon: React.ElementType }> = {
  low:          { label: 'Low Risk',               color: GovernanceColors.healthy,  icon: CheckCircle,   borderColor: GovernanceColors.healthy },
  medium:       { label: 'Medium Risk',            color: GovernanceColors.warning,  icon: AlertTriangle, borderColor: GovernanceColors.warning },
  high:         { label: 'High Risk',              color: GovernanceColors.critical, icon: XCircle,       borderColor: GovernanceColors.critical },
  critical:     { label: 'Critical Risk',          color: GovernanceColors.critical, icon: XCircle,       borderColor: GovernanceColors.critical },
  uncalibrated: { label: 'Baseline Not Established', color: '#A8A29E', icon: Info,   borderColor: '#4A443D' },
};

const STATUS_CONFIG: Record<DimensionExplanation['status'], { color: string; icon: React.ElementType }> = {
  normal:   { color: GovernanceColors.healthy, icon: CheckCircle },
  warning:  { color: GovernanceColors.warning, icon: AlertTriangle },
  critical: { color: GovernanceColors.critical, icon: XCircle },
};

function ScoreBar({ score, threshold }: { score: number; threshold: number }) {
  const pct = Math.min(100, Math.max(0, Math.round(score * 100)));
  const thresholdPct = Math.min(100, Math.max(0, Math.round(threshold * 100)));
  const color = score >= threshold ? GovernanceColors.healthy : score >= threshold * 0.85 ? GovernanceColors.warning : GovernanceColors.critical;
  return (
    <div className="relative h-2 bg-[#1C1917] rounded overflow-hidden" aria-hidden="true">
      <div className="absolute top-0 left-0 h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
      <div className="absolute top-0 h-full w-px bg-[#8F857B]" style={{ left: `${thresholdPct}%` }} />
    </div>
  );
}

export function ExplainabilityView() {
  const [status, setStatus] = useState<AnomalyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/anomaly/status', { headers: apiHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      // The endpoint reports { anomalies, baselineSize, recentAnomalies, timestamp }.
      // Normalize into the view model. Previously the view read overallRisk /
      // compositeScore / flaggedDimensions -- fields this endpoint never sends --
      // so it always rendered a misleading "Low Risk / 0.000 / no flagged
      // dimensions" regardless of state. Now the pre-baseline state is shown
      // honestly instead of defaulting missing fields to all-clear.
      const baselineSize = raw.baselineSize ?? 0;
      const anomalyCount = raw.anomalies ?? raw.anomalyCount ?? 0;
      const calibrated = baselineSize > 0;
      const normalized: AnomalyStatus = {
        active: raw.active ?? calibrated,
        overallRisk: raw.overallRisk ?? (!calibrated ? 'uncalibrated' : anomalyCount > 0 ? 'medium' : 'low'),
        compositeScore: raw.compositeScore ?? 0,
        flaggedDimensions: Array.isArray(raw.flaggedDimensions)
          ? raw.flaggedDimensions
          : (Array.isArray(raw.recentAnomalies) ? raw.recentAnomalies.filter((a: any) => a && a.dimension) : []),
        lastUpdated: raw.lastUpdated ?? raw.timestamp ?? '',
        sessionId: raw.sessionId,
        baselineSize,
        anomalyCount,
        calibrated,
      };
      setStatus(normalized);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load anomaly status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading anomaly status...</div>;
  }

  const risk = status?.overallRisk ?? 'low';
  const riskCfg = RISK_CONFIG[risk];
  const RiskIcon = riskCfg.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Anomaly Explainability</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchStatus(true)}
          disabled={refreshing}
          aria-label="Refresh anomaly status"
          className="border-[#4A443D] text-[#A8A29E] hover:text-[#F5F0E8] hover:bg-[#3D3833]"
        >
          <RefreshCw aria-hidden="true" className={`w-3 h-3 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-[#292524] border rounded-lg p-4" style={{ borderColor: `${GovernanceColors.critical}4D` }} role="alert">
          <AlertTriangle aria-hidden="true" className="w-5 h-5 shrink-0 mt-0.5" style={{ color: GovernanceColors.critical }} />
          <p className="text-xs" style={{ color: GovernanceColors.critical }}>{error}</p>
        </div>
      )}

      {status && (
        <>
          {/* Overall risk summary */}
          <div className="flex items-center gap-4 bg-[#292524] border rounded-lg p-4" style={{ borderColor: `${riskCfg.borderColor}66` }}>
            <RiskIcon
              aria-hidden="true"
              className="w-8 h-8 shrink-0"
              style={{ color: riskCfg.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold" style={{ color: riskCfg.color }}>{riskCfg.label}</span>
                <Badge variant="outline" className="text-xs" style={{ color: riskCfg.color, borderColor: riskCfg.borderColor }}>
                  {riskCfg.label}
                </Badge>
                <Badge variant="outline" className="text-xs" style={status.active ? { color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy } : { color: '#A8A29E', borderColor: '#4A443D' }}>
                  Detector {status.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-xs text-[#A8A29E] mt-1">
                Composite score: <span className="text-[#F5F0E8] font-mono">{(status.compositeScore ?? 0).toFixed(3)}</span>
                <span className="ml-3">Baseline: <span className="text-[#F5F0E8] font-mono">{status.baselineSize ?? 0}</span> sessions</span>
                <span className="ml-3">Anomalies: <span className="text-[#F5F0E8] font-mono">{status.anomalyCount ?? 0}</span></span>
                {status.lastUpdated && (
                  <span className="ml-3 text-[#8F857B]">Updated {new Date(status.lastUpdated).toLocaleString()}</span>
                )}
              </p>
              {status.calibrated === false && (
                <p className="text-xs mt-1" style={{ color: GovernanceColors.warning }}>
                  Detector not yet calibrated -- a per-agent baseline is established after agents accumulate session history. Until then, no risk score can be computed.
                </p>
              )}
            </div>
          </div>

          {/* Flagged dimensions */}
          {status.flaggedDimensions && status.flaggedDimensions.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-[#A8A29E]">
                Flagged Dimensions ({status.flaggedDimensions.length})
              </h3>
              {status.flaggedDimensions.map((dim, i) => {
                const dimCfg = STATUS_CONFIG[dim.status];
                const DimIcon = dimCfg.icon;
                return (
                  <Card key={i} className="bg-[#292524] border-[#3A352F]">
                    <CardContent className="p-4 space-y-3">
                      {/* Dimension header */}
                      <div className="flex items-start gap-2">
                        <DimIcon
                          aria-hidden="true"
                          className="w-4 h-4 mt-0.5 shrink-0"
                          style={{ color: dimCfg.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[#F5F0E8]">{dim.dimension}</span>
                            <Badge
                              variant="outline"
                              className="text-xs px-1 py-0"
                              style={{ color: dimCfg.color, borderColor: dimCfg.color }}
                            >
                              {dim.status}
                            </Badge>
                          </div>

                          {/* Score bar */}
                          <div className="mt-2 space-y-1">
                            <div className="flex justify-between text-xs text-[#A8A29E]">
                              <span>Score <span className="font-mono text-[#F5F0E8]">{(dim.score ?? 0).toFixed(3)}</span></span>
                              <span>Threshold <span className="font-mono text-[#F5F0E8]">{(dim.threshold ?? 0).toFixed(3)}</span></span>
                            </div>
                            <ScoreBar score={dim.score} threshold={dim.threshold} />
                            <p className="text-xs text-[#8F857B]">
                              Bar shows score vs threshold -- vertical line marks threshold
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Human-readable explanation */}
                      <div className="flex items-start gap-2">
                        <Info aria-hidden="true" className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: GovernanceColors.streaming }} />
                        <p className="text-xs text-[#A8A29E]">{dim.explanation}</p>
                      </div>

                      {/* Recommendations */}
                      {dim.recommendations && dim.recommendations.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-[#A8A29E]">Recommendations</p>
                          <ul className="space-y-1">
                            {dim.recommendations.map((rec, j) => (
                              <li key={j} className="flex items-start gap-2 text-xs text-[#F5F0E8]">
                                <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ color: GovernanceColors.primary }}>&#8250;</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-[#292524] border-[#3A352F]">
              <CardContent className="p-6 flex flex-col items-center gap-3 text-center">
                {status.calibrated === false ? (
                  <>
                    <Info aria-hidden="true" className="w-8 h-8 text-[#A8A29E]" />
                    <p className="text-sm text-[#A8A29E]">No baseline established yet -- anomaly dimensions cannot be evaluated until agents accumulate session history.</p>
                  </>
                ) : (
                  <>
                    <CheckCircle aria-hidden="true" className="w-8 h-8" style={{ color: GovernanceColors.healthy }} />
                    <p className="text-sm text-[#A8A29E]">No flagged dimensions -- all anomaly checks within normal range.</p>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {status.sessionId && (
            <p className="text-xs text-[#8F857B]">Session: <span className="font-mono">{status.sessionId}</span></p>
          )}
        </>
      )}
    </div>
  );
}
